const MODULE_OBJECT = "module";
var SecurityContext = /* @__PURE__ */ ((SecurityContext2) => {
  SecurityContext2["USER"] = "USER";
  SecurityContext2["PARTNER"] = "PARTNER";
  return SecurityContext2;
})(SecurityContext || {});
var OnDuplicate = /* @__PURE__ */ ((OnDuplicate2) => {
  OnDuplicate2["THROW"] = "throw";
  OnDuplicate2["IGNORE"] = "ignore";
  return OnDuplicate2;
})(OnDuplicate || {});
class ScriptingObjectManager {
  #normalizeId = (id) => id.trim().toLowerCase();
  /**
   * collection of registered scripting objects
   */
  #scriptingObjects = /* @__PURE__ */ new Map();
  /**
   * collection of proxied scripting objects for each guest
   */
  #guestScriptingObjects = /* @__PURE__ */ new Map();
  #addGuestScriptingObject = (params) => {
    const {
      so,
      guestId,
      onDuplicate = "throw" /* THROW */,
      securityContext
    } = params;
    const objectId = this.#normalizeId(so.id);
    const soInfo = { so, guestId, securityContext };
    const guestSOs = this.#guestScriptingObjects.get(guestId);
    if (!guestSOs) {
      this.#guestScriptingObjects.set(guestId, /* @__PURE__ */ new Map([[objectId, soInfo]]));
      return;
    }
    if (guestSOs.has(objectId)) {
      if (onDuplicate === "ignore" /* IGNORE */) return;
      throw new Error(
        `Scripting Object ${so.id} already exists for guest ${guestId}`
      );
    }
    guestSOs.set(objectId, soInfo);
  };
  #disposeSO = ({ so }) => {
    if (so._dispose && typeof so._dispose === "function") {
      try {
        so._dispose();
      } catch {
      }
    }
  };
  /**
   * get scripting object for the given object id and guest id
   * this method is backward compatible with the V1 guest, where the guest id is not provided. It will return the scripting object for the first guest
   * @param root0
   * @param root0.objectId
   * @param root0.guestId
   * @returns scripting object for the given object id and guest id. null if not found
   */
  #getGuestScriptingObject = ({
    objectId,
    guestId
  }) => {
    if (objectId === MODULE_OBJECT && !guestId) {
      for (const [, mSOs] of this.#guestScriptingObjects) {
        const mSO = mSOs.get(objectId);
        if (mSO) return mSO;
      }
    }
    const SOs = guestId ? this.#guestScriptingObjects.get(guestId) : null;
    if (SOs) {
      return SOs.get(objectId) ?? null;
    }
    return null;
  };
  #removeGuestScriptingObject = ({
    objectId,
    guestId
  } = {}) => {
    if (guestId) {
      if (!objectId) {
        const sosInfo = this.#guestScriptingObjects.get(guestId);
        if (sosInfo) {
          sosInfo.forEach(this.#disposeSO);
        }
        this.#guestScriptingObjects.delete(guestId);
        return;
      }
      const moduleSOs = this.#guestScriptingObjects.get(guestId);
      if (moduleSOs) {
        const soInfo = moduleSOs.get(objectId);
        if (soInfo) this.#disposeSO(soInfo);
        moduleSOs.delete(objectId);
      }
    } else if (objectId) {
      this.#guestScriptingObjects.forEach((moduleSOs) => {
        const soInfo = moduleSOs.get(objectId);
        if (soInfo) this.#disposeSO(soInfo);
        moduleSOs.delete(objectId);
      });
    }
  };
  /**
   * registers scripting object to the host
   * @param so scripting object to register
   * @param {AddScriptingObjectParams<ValueOf<AppObjects>>}
   * @param params
   */
  addScriptingObject = (so, params) => {
    const {
      guestId,
      onDuplicate = "throw" /* THROW */,
      securityContext
    } = params || {};
    if (!so?.id || !so?._toJSON) {
      throw new Error("Object is not derived from ScriptingObject");
    }
    const objectId = this.#normalizeId(so.id);
    if (objectId === MODULE_OBJECT && !guestId) {
      throw new Error(`Guest id is required to add Module scripting object`);
    }
    if (guestId) {
      this.#addGuestScriptingObject({ so, ...params, guestId, onDuplicate });
      return;
    }
    if (this.#scriptingObjects.has(objectId)) {
      if (onDuplicate === "ignore" /* IGNORE */) return;
      throw new Error(`Scripting Object ${so.id} already exists`);
    }
    this.#scriptingObjects.set(objectId, { so, guestId, securityContext });
  };
  /**
   * Get reference to the scripting object proxy for a guest
   * @param {string} objectId scripting object id
   * @param {GuestContext} guest that is requesting the scripting object
   * @returns proxied scripting object reference
   */
  getObject = (objectId, guest) => {
    const id = this.#normalizeId(objectId);
    let soInfo = this.#getGuestScriptingObject({
      objectId: id,
      guestId: guest?.id
    });
    soInfo = soInfo ?? this.#scriptingObjects.get(id) ?? null;
    const { so } = soInfo || {};
    if (!so) return null;
    return so;
  };
  /**
   * Check if a scripting object exists
   * @param objectId unique id of the scripting object
   * @param guest
   * @returns true if the scripting object exists, false otherwise
   */
  has = (objectId, guest) => this.getObject(objectId, guest) !== null;
  /**
   * list name of scripting objects for the given guest
   * @param guestId unique id of the guest
   * @returns name of scripting objects for the given guest
   */
  listScriptingObjects = (guestId) => {
    const objects = new Set(this.#scriptingObjects.keys());
    const guestSOs = this.#guestScriptingObjects.get(guestId);
    if (guestSOs) {
      for (const key of guestSOs.keys()) objects.add(key);
    }
    return Array.from(objects);
  };
  /**
   * removes scripting object
   * @param objectId unique id of the scripting object
   * @param guestId unique id of the guest
   */
  removeScriptingObject = (objectId, guestId) => {
    const id = this.#normalizeId(objectId);
    if (guestId) {
      this.#removeGuestScriptingObject({ objectId: id, guestId });
    } else {
      this.#removeGuestScriptingObject({ objectId: id });
      const soInfo = this.#scriptingObjects.get(id);
      if (soInfo) this.#disposeSO(soInfo);
      this.#scriptingObjects.delete(id);
    }
  };
  /**
   * removes all scripting objects
   * @param guestId unique id of the guest
   */
  removeAllScriptingObjects = (guestId) => {
    if (!guestId) {
      this.#scriptingObjects.forEach(this.#disposeSO);
      this.#scriptingObjects.clear();
    } else {
      this.#removeGuestScriptingObject({ guestId });
    }
  };
}
export {
  MODULE_OBJECT,
  OnDuplicate,
  ScriptingObjectManager,
  SecurityContext
};
